/**
 * Sub-Batch 3R — Inline result summary card
 * (Niranjan, Feb 27 2026)
 *
 * Rendered immediately after a fresh analysis completes in the
 * Biometrical Analysis page (and any other page that previously dropped
 * the analysis-content payload on the floor and only kept the analysis_id).
 *
 * Pattern matches the canonical inline-below-trigger flow used by RBD,
 * CRD, Factorial, Augmented, etc.  A "View full results" CTA opens the
 * full /results/{id} page in a new tab so the user can drill into long
 * tables / charts without leaving the upload flow.
 *
 * Pattern 12 — no recomputation.  We render whatever the engine surfaced
 * (anova_table, summary stats, key per-trait metrics).  Design-specific
 * renderers fall back to a generic key-value preview when the result
 * shape isn't recognised.
 */
import React, { useMemo } from 'react';
import { CheckCircle2 } from 'lucide-react';

const STAT_PILL = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
  background: 'rgba(45,106,79,0.10)', color: 'var(--green-900)',
};

function _firstNumber(...candidates) {
  for (const c of candidates) {
    if (typeof c === 'number' && Number.isFinite(c)) return c;
    const n = parseFloat(c);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function _fmt(n, places = 4) {
  if (n === null || n === undefined) return '—';
  if (typeof n === 'number') return n.toFixed(places);
  return String(n);
}

function _extractHighlights(designKey, results) {
  /* eslint-disable no-fallthrough */
  if (!results || typeof results !== 'object') return [];

  // Look through ALL result roots — some endpoints store under
  // results.<design_key>, others (like augmented) under results.augmented.
  const root = (typeof results === 'object' && results) || {};
  const candidates = [root];
  for (const v of Object.values(root)) {
    if (v && typeof v === 'object' && !Array.isArray(v)) candidates.push(v);
  }

  // Try design-specific highlights first
  for (const node of candidates) {
    switch (designKey) {
      case 'pca-bio':
      case 'pca':
        // Engine emits pca.eigenvalues as a dict keyed by 'PC1'/'PC2'/...
        // each carrying {eigenvalue, variance_percent, cumulative_percent}.
        if (node?.eigenvalues && typeof node.eigenvalues === 'object'
            && !Array.isArray(node.eigenvalues)) {
          const pcKeys = Object.keys(node.eigenvalues)
            .filter(k => /^PC\d+$/i.test(k))
            .sort((a, b) => parseInt(a.replace(/PC/i, ''), 10) - parseInt(b.replace(/PC/i, ''), 10));
          if (pcKeys.length > 0) {
            const pc1 = node.eigenvalues[pcKeys[0]] || {};
            const lastK = Math.min(3, pcKeys.length);
            const pcLast = node.eigenvalues[pcKeys[lastK - 1]] || {};
            return [
              { label: 'Components retained', value: pcKeys.length },
              { label: 'PC1 variance', value: `${_fmt(pc1.variance_percent, 2)}%` },
              { label: `Top-${lastK} cumulative`, value: `${_fmt(pcLast.cumulative_percent, 2)}%` },
            ];
          }
        }
        // Fallback for legacy array shapes
        if (Array.isArray(node?.explained_variance)) {
          const ev = node.explained_variance;
          return [
            { label: 'Components retained', value: ev.length },
            { label: 'Variance by PC1', value: `${_fmt((ev[0] || 0) * 100, 2)}%` },
          ];
        }
        break;
      case 'correlation':
      case 'multiple-corr': {
        // Engine emits genotypic_correlation_matrix + phenotypic_correlation_matrix
        // as `{trait1: {trait2: {value, p_value}}}` structures.
        const matKey = ['phenotypic_correlation_matrix', 'genotypic_correlation_matrix',
                        'correlation_matrix', 'correlations'].find(k => node?.[k]);
        if (matKey) {
          const mat = node[matKey];
          let n = 0; let sig = 0;
          if (Array.isArray(mat)) n = mat.length;
          else if (mat && typeof mat === 'object') {
            n = Object.keys(mat).length;
            for (const inner of Object.values(mat)) {
              if (inner && typeof inner === 'object') {
                for (const cell of Object.values(inner)) {
                  const p = cell && typeof cell === 'object'
                    ? parseFloat(cell.p_value) : NaN;
                  if (Number.isFinite(p) && p < 0.05) sig += 1;
                }
              }
            }
            sig = Math.max(0, Math.round((sig - n) / 2));  // symmetric off-diagonal
          }
          return [
            { label: 'Variables', value: n },
            { label: 'Matrix', value: matKey.replace(/_correlation_matrix$/, '') },
            { label: 'Significant pairs (p<0.05)', value: sig || '—' },
          ];
        }
        break;
      }
      case 'regression':
      case 'multiple-reg': {
        const r2 = _firstNumber(node?.r_squared, node?.R2,
                                node?.model_summary?.r_squared);
        if (r2 !== null) {
          return [
            { label: 'R²', value: _fmt(r2, 4) },
            { label: 'Adjusted R²', value: _fmt(_firstNumber(node?.adjusted_r_squared, node?.adj_r_squared), 4) },
            { label: 'Std. Error', value: _fmt(_firstNumber(node?.standard_error, node?.std_error), 4) },
          ];
        }
        break;
      }
      case 'd2-cluster':
      case 'd_squared': {
        // Engine emits cluster_analysis with nested tocher/ward/upgma/kmeans
        if (node?.tocher || node?.ward || node?.upgma || node?.n_genotypes) {
          const method = node.tocher ? 'D² + Tocher'
                       : node.ward   ? 'D² + Ward'
                       : node.upgma  ? 'D² + UPGMA'
                       : 'D²';
          const clusters = node?.tocher?.n_clusters
                        || node?.ward?.n_clusters
                        || node?.upgma?.n_clusters
                        || node?.n_clusters;
          return [
            { label: 'Genotypes', value: node?.n_genotypes ?? '—' },
            { label: 'Traits', value: node?.n_traits ?? '—' },
            { label: 'Clusters', value: clusters ?? '—' },
            { label: 'Method', value: method },
          ];
        }
        break;
      }
      case 'discriminant': {
        if (node?.classification_accuracy !== undefined) {
          return [
            { label: 'Classification accuracy', value: `${_fmt(node.classification_accuracy * 100, 2)}%` },
            { label: 'Functions', value: node.n_functions || '—' },
            { label: 'Groups', value: node.n_groups || '—' },
          ];
        }
        break;
      }
      case 'path': {
        if (node?.direct_effects || node?.path_coefficients) {
          const direct = node.path_coefficients || node.direct_effects;
          const n = Array.isArray(direct) ? direct.length : Object.keys(direct).length;
          return [
            { label: 'Causal variables', value: n || '—' },
            { label: 'R²', value: _fmt(node.r_squared, 4) },
            { label: 'Residual effect', value: _fmt(node.residual_effect, 4) },
          ];
        }
        break;
      }
      default:
        break;
    }
  }
  /* eslint-enable no-fallthrough */

  // Generic fallback — first 4 numeric / scalar top-level fields
  const flat = [];
  for (const node of candidates) {
    for (const [k, v] of Object.entries(node)) {
      if (flat.length >= 4) break;
      if (k === '_id' || k === 'id') continue;
      if (typeof v === 'number') flat.push({ label: k, value: _fmt(v) });
      else if (typeof v === 'string' && v.length < 60) flat.push({ label: k, value: v });
    }
    if (flat.length >= 4) break;
  }
  return flat;
}


export function InlineResultSummary({ data, designKey, designLabel, color }) {
  const highlights = useMemo(
    () => _extractHighlights(designKey, data?.results),
    [data, designKey],
  );
  if (!data?.id) return null;
  const accent = color || '#2D6A4F';

  return (
    <div
      className="rounded-xl border-2 p-5 mt-4"
      style={{ background: 'rgba(45,106,79,0.04)', borderColor: accent }}
      data-testid={`inline-result-summary-${designKey}`}
    >
      <div className="flex items-start gap-3 mb-3">
        <CheckCircle2 size={22} style={{ color: accent, flexShrink: 0 }} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm" style={{ color: 'var(--green-900)' }}>
            {designLabel || designKey} analysis complete
          </div>
          <div className="text-xs mt-0.5" style={{ color: 'var(--text-medium)' }}>
            Analysis ID: <span className="font-mono">{data.id.slice(0, 12)}…</span>
            {' · '}
            {data.created_at && new Date(data.created_at).toLocaleString()}
          </div>
        </div>
      </div>

      {highlights.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="inline-result-highlights">
          {highlights.map((h, i) => (
            <div key={i} style={STAT_PILL}>
              <span style={{ opacity: 0.7 }}>{h.label}:</span>
              <span>{h.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default InlineResultSummary;
